import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Custom TranslateLoader that merges regional English variants with the base en_US translations.
 * For en_GB, en_AU, en_CA: Loads en_US as base, then merges regional overrides on top.
 * For all other locales: Loads the locale file directly.
 */
export class MergeTranslateLoader implements TranslateLoader {
  private readonly ENGLISH_VARIANTS = ['en_GB', 'en_AU', 'en_CA'];
  private readonly BASE_ENGLISH = 'en_US';

  constructor(
    private http: HttpClient,
    private prefix: string = './assets/i18n/',
    private suffix: string = '.json'
  ) {}

  getTranslation(lang: string): Observable<TranslationObject> {
    // Normalize English regional variants to correct case (e.g., en_au → en_AU)
    const normalized = this.normalizeEnglishVariant(lang);

    if (this.ENGLISH_VARIANTS.includes(normalized)) {
      return this.loadMergedTranslations(normalized);
    }

    // For all other languages, load directly
    return this.http.get<TranslationObject>(`${this.prefix}${lang}${this.suffix}`);
  }

  /**
   * Normalizes English variant locale codes to correct case (en_au → en_AU)
   */
  private normalizeEnglishVariant(lang: string): string {
    if (lang?.startsWith('en_') && lang.length === 5) {
      return `en_${lang.substring(3).toUpperCase()}`;
    }
    return lang;
  }

  /**
   * Loads base en_US translations and merges regional overrides on top
   */
  private loadMergedTranslations(regionalLang: string): Observable<TranslationObject> {
    const baseUrl = `${this.prefix}${this.BASE_ENGLISH}${this.suffix}`;
    const overrideUrl = `${this.prefix}${regionalLang}${this.suffix}`;

    return forkJoin([
      this.http.get<TranslationObject>(baseUrl),
      this.http.get<TranslationObject>(overrideUrl).pipe(
        // If regional override file doesn't exist, use empty object (graceful fallback)
        catchError(() => of({} as TranslationObject))
      )
    ]).pipe(
      map(([base, overrides]) => this.deepMerge(base, overrides))
    );
  }

  /**
   * Deep merges two objects, with source properties overriding target
   */
  private deepMerge(target: TranslationObject, source: TranslationObject): TranslationObject {
    const result = { ...target };

    for (const key of Object.keys(source)) {
      const sourceValue = source[key];
      const targetValue = result[key];

      if (this.isObject(sourceValue) && this.isObject(targetValue)) {
        result[key] = this.deepMerge(targetValue, sourceValue);
      } else {
        result[key] = sourceValue;
      }
    }

    return result;
  }

  /**
   * Type guard to check if a value is a plain object
   */
  private isObject(item: unknown): item is TranslationObject {
    return item !== null && typeof item === 'object' && !Array.isArray(item);
  }
}
